import { useState } from "react";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { bookKey, getBook, tagOptions, updateBook, type Book, type Tag } from "../api";
import { useT } from "../i18n";

const pill = (on: boolean) =>
  `rounded-full border px-3 py-1 text-xs transition-colors ${
    on ? "border-sage bg-sage-soft font-medium" : "border-line hover:bg-sunk"
  }`;

/**
 * BookTagsEditor sets a book's tags (requirement 0119 T3): pill chips, each
 * removable, and a search over the tags this library can already see, or a
 * new one made from what was typed. Tags are shared by every library and
 * matched by key, so typing a known word in another case finds it rather than
 * making a second. Every change saves the whole list (`tags` on the book's
 * PATCH), under book.update.
 */
export default function BookTagsEditor({ bookID }: { bookID: string }) {
  const t = useT();
  const client = useQueryClient();
  const book = useQuery({
    queryKey: bookKey(bookID, { draft: true }),
    queryFn: () => getBook(bookID, { draft: true }),
  });
  const [query, setQuery] = useState("");
  const typed = query.trim();
  const options = useQuery({
    queryKey: ["tag-options", bookID, typed],
    queryFn: () => tagOptions(bookID, typed),
    placeholderData: keepPreviousData,
    staleTime: 10_000,
  });
  const save = useMutation({
    mutationFn: (choices: ({ id: string } | { name: string })[]) =>
      updateBook(bookID, { tags: choices }),
    onSuccess: (updated: Book) => {
      client.setQueryData(bookKey(bookID, { draft: true }), updated);
      void client.invalidateQueries({ queryKey: ["book", bookID] });
      void client.invalidateQueries({ queryKey: ["tag-options", bookID] });
      void client.invalidateQueries({ queryKey: ["tags"] });
    },
  });

  const tags: Tag[] = book.data?.tags ?? [];
  const busy = save.isPending || book.isPending;
  const ids = tags.map((tag) => ({ id: tag.id }));
  const add = (choice: { id: string } | { name: string }) => {
    save.mutate([...ids, choice]);
    setQuery("");
  };
  const fold = (s: string) => s.normalize("NFC").toLocaleLowerCase();
  const found = (options.data?.items ?? []).filter((o) => !tags.some((tag) => tag.id === o.id));
  const exact = [...tags, ...found].some((o) => fold(o.name) === fold(typed));

  return (
    <div data-testid="book-tags-editor">
      <p className="block text-sm font-medium">{t.tag.tags}</p>
      <p className="mt-1 mb-3 text-xs text-muted">{t.tag.note}</p>

      {tags.length === 0 && !book.isPending && (
        <p data-testid="tags-none" className="mb-3 text-sm text-muted">
          {t.tag.none}
        </p>
      )}
      <ul className="mb-3 flex flex-wrap gap-2 empty:hidden">
        {tags.map((tag) => (
          <li key={tag.id}>
            <button
              type="button"
              dir="auto"
              data-testid="book-tag-remove"
              aria-label={t.tag.remove(tag.name)}
              disabled={busy}
              onClick={() => save.mutate(ids.filter((c) => c.id !== tag.id))}
              className={pill(true)}
            >
              {tag.name} <span aria-hidden="true">×</span>
            </button>
          </li>
        ))}
      </ul>

      <input
        dir="auto"
        type="search"
        data-testid="tag-search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={t.tag.search}
        className="mb-2 w-full rounded-control border border-line bg-surface px-3 py-2 text-sm"
      />
      <div className="flex flex-wrap gap-2" data-testid="tag-options">
        {found.map((o) => (
          <button
            key={o.id}
            type="button"
            dir="auto"
            data-testid="tag-option"
            disabled={busy}
            onClick={() => add({ id: o.id })}
            className={pill(false)}
          >
            {o.name}
          </button>
        ))}
        {typed && !exact && (
          <button
            type="button"
            data-testid="tag-create"
            disabled={busy}
            onClick={() => add({ name: typed })}
            className={pill(false)}
          >
            {t.tag.create(typed)}
          </button>
        )}
      </div>
      {options.isError && (
        <p role="alert" className="mt-2 text-sm text-red-600 dark:text-red-400">
          {t.tag.searchFailed}
        </p>
      )}
      {save.isError && (
        <p
          role="alert"
          data-testid="tags-error"
          className="mt-2 text-sm text-red-600 dark:text-red-400"
        >
          {save.error instanceof Error ? save.error.message : t.tag.saveFailed}
        </p>
      )}
    </div>
  );
}
